import { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid2";

import AlertBanner from "lib/components/alert/AlertBanner";
import AlertMessage from "lib/components/alert/AlertMessage";
import FeedbackButton from "lib/components/button/FeedbackButton";
import FileUpload from "lib/components/form/FileUpload";

import { asTitle, isEmailValid } from "utils/Utils";
import useFetch from "utils/fetch/useFetch";

import { UserRole, UserRoleKey } from "organisation/OrganisationTypes";
import OrganisationUserClient from "./OrganisationUserClient";
import { Overview } from "./OrganisationUserTypes";

type OrganisationUser = {
	email: string;
	role: UserRoleKey;
};

export type ImportOrganisationUsersDialogProps = {
	orgId: number;
	existingUsers: Array<Overview>;

	onClose: () => void;
	onImported: (users: Array<Overview>) => void;
};

export default function ImportOrganisationUsersDialog(
	props: Readonly<ImportOrganisationUsersDialogProps>
): JSX.Element {
	const { t } = useTranslation();

	const { call } = useFetch();

	const [alert, setAlert] = useState<AlertMessage>();
	const [users, setUsers] = useState<Array<OrganisationUser>>([]);

	const roles = useMemo(
		() =>
			new Map(
				Object.values(UserRole).map(value => [
					t(value).toLowerCase(),
					value
				])
			),
		[]
	);

	const handleLoadData = useCallback(
		(data: Array<Array<string>>): void => {
			setUsers(
				data
					.map(row => ({
						email: row[0],
						role: roles.get(row[1]?.toLowerCase()) ?? UserRole.USER
					}))
					.filter(
						user =>
							isEmailValid(user.email) &&
							!props.existingUsers.some(
								existing => existing.email === user.email
							)
					)
			);
		},
		[props.existingUsers]
	);

	const handleCreate = useCallback((): Promise<void> => {
		closeAlert();

		// TODO: would be better to show a progress bar with a number and such.
		return Promise.allSettled(
			users.map(user =>
				call(
					OrganisationUserClient.create(props.orgId, {
						email: user.email,
						role: user.role
					})
				)
			)
		)
			.then(results =>
				props.onImported(
					results
						.filter(result => result.status === "fulfilled")
						.map(result => result.value)
				)
			)
			.catch(() => {
				setAlert(
					AlertMessage.error(
						t("importObjectError", { object: "user" })
					)
				);
			});
	}, [users, props.orgId, props.onImported]);

	const closeAlert = useCallback((): void => {
		setAlert(undefined);
	}, []);

	return (
		<Dialog
			open
			fullWidth
			maxWidth="sm"
			scroll="paper"
			onClose={props.onClose}>
			<DialogTitle>
				{asTitle(t("importObject", { object: "user_other" }))}
			</DialogTitle>
			<DialogContent>
				<Grid container direction="column" spacing={1}>
					<Grid>
						{alert && (
							<AlertBanner alert={alert} onClose={closeAlert} />
						)}
					</Grid>
					<Grid>
						<FileUpload
							label={t("dataFile")}
							onLoadData={handleLoadData}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onClose}>
					{t("cancel")}
				</Button>
				<FeedbackButton
					variant="contained"
					label={t("import")}
					onClick={handleCreate}
				/>
			</DialogActions>
		</Dialog>
	);
}
